/**
 * PendingBadge — header bell icon with live count of PRs awaiting the
 * logged-in user's action across three roles (authorise / approve / requestor
 * review). Polls /api/me/pending-counts every 60 s.
 *
 * Replaces the WhatsApp ping to L1/L2 — they now see this badge in the app
 * instead of getting a Twilio message.
 *
 *   • Bell icon turns red when total > 0.
 *   • Click → small dropdown showing breakdown + link to dashboard.
 *   • Zero count → grey bell, no dot.
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";

const POLL_MS = 60_000;

export default function PendingBadge() {
  const [counts, setCounts] = useState({ authorise: 0, approve: 0, requestor_review: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState("unknown"); // unknown | unsupported | denied | enabled | disabled
  const [pushErr, setPushErr] = useState("");
  const ref = useRef(null);
  const nav = useNavigate();

  // Fetch loop — silent failures (we don't want to spam toasts if MS SQL blips).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/me/pending-counts");
        if (!cancelled && data) setCounts(data);
      } catch (err) {
        // Non-fatal — keep last known counts visible
        console.warn("[PendingBadge] /me/pending-counts fetch failed", err);
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const total = counts.total || 0;
  const hot = total > 0;

  // Detect existing push subscription on mount.
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    if (Notification.permission === "denied") { setPushState("denied"); return; }
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setPushState(sub ? "enabled" : "disabled");
    }).catch(() => setPushState("disabled"));
  }, []);

  // Enable phone notifications — asks browser permission, registers push subscription,
  // saves it to the backend so we can target this device later.
  const enablePush = async () => {
    setPushErr("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setPushState("unsupported");
        setPushErr("Browser does not expose ServiceWorker / PushManager");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setPushState(perm === "denied" ? "denied" : "disabled");
        setPushErr(`Permission: ${perm}`);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data: { key } } = await api.get("/push/vapid-public-key");
      // If a stale subscription exists (e.g. from a previously rotated VAPID key),
      // drop it so we can register fresh with the current key.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try {
          await existing.unsubscribe();
        } catch (err) {
          console.warn("[PendingBadge] stale push subscription unsubscribe failed", err);
        }
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
      await api.post("/push/subscribe", { ...sub.toJSON(), ua: navigator.userAgent });
      setPushState("enabled");
    } catch (e) {
      console.error("push enable failed", e);
      setPushState("disabled");
      const msg = (e && (e.message || e.toString())) || "unknown error";
      setPushErr(msg.slice(0, 180));
    }
  };

  const sendTestPush = async () => {
    setPushErr("");
    try {
      const { data } = await api.post("/push/test");
      if (data && data.sent === 0) setPushErr("Server delivered 0 messages — check VAPID config");
    } catch (e) {
      console.error(e);
      setPushErr((e && (e.message || e.toString())) || "test failed");
    }
  };

  const goTo = (mode) => {
    setOpen(false);
    if (mode === "requestor_review") nav("/pr-review");
    else nav(`/dashboard?mode=${mode}`);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="pending-badge-btn"
        title={total ? `${total} pending` : "No pending actions"}
        className={`relative h-8 px-2 rounded inline-flex items-center justify-center border transition-colors
          ${hot ? "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
                : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}
      >
        <Bell size={16} />
        {hot && (
          <span
            data-testid="pending-badge-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center leading-none"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="pending-badge-dropdown"
          className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-50 text-sm"
        >
          <div className="text-xs uppercase tracking-wider text-slate-500 px-2 py-1">Awaiting your action</div>
          <Row label="To authorise (L1)" count={counts.authorise} onClick={() => goTo("authorise")} testid="pending-row-authorise" />
          <Row label="To approve (L2)"  count={counts.approve}    onClick={() => goTo("approve")}    testid="pending-row-approve" />
          <Row label="To verify (Requestor)" count={counts.requestor_review} onClick={() => goTo("requestor_review")} testid="pending-row-requestor" />
          {total === 0 && (
            <div className="text-xs text-slate-500 px-2 py-3 text-center">You&apos;re all caught up. 🎉</div>
          )}
          {/* Phone notifications setup — VAPID web push so L1/L2 get phone
              notification-bar pings without WhatsApp. */}
          <div className="border-t border-slate-200 mt-2 pt-2 px-2">
            {pushState === "enabled" && (
              <div className="flex items-center justify-between gap-2 text-[11px] text-emerald-700">
                <span>📱 Phone notifications ON</span>
                <button type="button" onClick={sendTestPush} data-testid="push-test"
                        className="underline hover:text-emerald-900">Send test</button>
              </div>
            )}
            {pushState === "disabled" && (
              <button type="button" onClick={enablePush} data-testid="push-enable"
                      className="w-full text-[12px] text-cyan-700 font-semibold py-1.5 rounded hover:bg-cyan-50">
                📱 Enable phone notifications
              </button>
            )}
            {pushState === "denied" && (
              <div className="text-[11px] text-slate-500">📱 Notifications blocked in browser settings</div>
            )}
            {pushState === "unsupported" && (
              <div className="text-[11px] text-slate-500">📱 Phone push not supported on this browser</div>
            )}
            {pushErr && (
              <div data-testid="push-error" className="mt-1 text-[10px] text-rose-600 break-words">
                ⚠ {pushErr}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Convert base64url-encoded VAPID public key → Uint8Array for PushManager.subscribe.
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function Row({ label, count, onClick, testid }) {
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center justify-between px-2 py-2 rounded hover:bg-slate-50 transition-colors"
    >
      <span className="text-slate-700">{label}</span>
      <span className="inline-flex min-w-[22px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold items-center justify-center">
        {count}
      </span>
    </button>
  );
}
